import React, { useImperativeHandle, useRef } from 'react';

interface StripeInputProps {
    // A Stripe Element component, e.g. CardNumberElement.
    readonly component: React.ElementType;
    readonly options?: Record<string, any>;
    readonly onChange?: (event: any) => void;
}

// Lets a Stripe Element sit inside a MUI TextField, so the fallback card form keeps the
// outlined border, label, adornments and helper text used everywhere else in the
// booking form. MUI drives its custom inputs through a ref exposing `focus()`, which
// Stripe's own element instance provides once it's mounted.
//
// Note MUI's contract is `{ focus(): void; value?: string }` and Stripe deliberately
// never reports a value, so any TextField using this needs the label pinned with
// InputLabelProps={{ shrink: true }} - otherwise it sits over the card number.
const StripeInput = React.forwardRef<unknown, StripeInputProps>(({ component: Component, ...props }, ref) => {
    const elementRef = useRef<any>(null);

    useImperativeHandle(ref, () => ({
        focus: () => elementRef.current?.focus(),
    }));

    const handleReady = (element: any) => {
        elementRef.current = element;
    };

    return <Component onReady={handleReady} {...props} />;
});

StripeInput.displayName = 'StripeInput';

export default StripeInput;
